import React, { useEffect, useState } from "react";
import { Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../contexts/LanguageContext";
import { api } from "../../lib/api";

const Messages = () => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const load = async () => {
    const params = filter !== "all" ? { status_filter: filter } : {};
    const { data } = await api.get("/admin/messages", { params });
    setItems(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/messages/${id}`, { status });
    toast.success(t.admin.saved);
    load();
    if (active && active.id === id) setActive({ ...active, status });
  };

  const remove = async (id) => {
    if (!window.confirm(t.admin.confirm)) return;
    await api.delete(`/admin/messages/${id}`);
    toast.success(t.admin.deleted);
    setActive(null);
    load();
  };

  const exportCsv = () => {
    const rows = [
      ["Date", "Name", "Email", "Phone", "Company", "Service", "Budget", "Status", "Message"],
      ...items.map((m) => [m.created_at, m.name, m.email, m.phone || "", m.company || "", m.service || "", m.budget || "", m.status, (m.message || "").replace(/[\n\r]/g, " ")]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `vakar-messages-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div data-testid="admin-messages">
      <h1 className="font-display" style={{ fontSize: "2.5rem", fontWeight: 300, color: "var(--warm-off-white)", marginBottom: 24 }}>{t.admin.nav.messages}</h1>
      <div className="flex flex-wrap items-center justify-between" style={{ gap: 16, marginBottom: 24 }}>
        <div className="flex" style={{ gap: 8 }}>
          {["all", "new", "read", "replied"].map((s) => (
            <button key={s} data-testid={`msg-filter-${s}`} onClick={() => setFilter(s)} className="font-mono" style={{ background: "transparent", border: "1px solid", borderColor: filter === s ? "var(--gold)" : "rgba(248,244,240,0.15)", color: filter === s ? "var(--gold)" : "rgba(248,244,240,0.7)", padding: "8px 16px", textTransform: "uppercase", letterSpacing: "0.15em", fontSize: "0.7rem", cursor: "pointer" }}>
              {t.admin.status[s]}
            </button>
          ))}
        </div>
        <button data-testid="export-csv" onClick={exportCsv} className="font-mono" style={{ background: "transparent", border: "1px solid rgba(201,151,58,0.4)", color: "var(--gold)", padding: "8px 16px", textTransform: "uppercase", letterSpacing: "0.15em", fontSize: "0.7rem", cursor: "pointer" }}>Export CSV</button>
      </div>
      <div style={{ background: "var(--rich-black-light)", border: "1px solid rgba(139,32,53,0.25)", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "rgba(139,32,53,0.1)" }}>
              {["Date", "Name", "Email", "Service", "Budget", "Status", ""].map((h) => (
                <th key={h} className="font-mono" style={{ textAlign: "left", padding: 14, fontSize: "0.7rem", letterSpacing: "0.18em", color: "var(--gold)", textTransform: "uppercase", fontWeight: 400 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((m) => (
              <tr key={m.id} data-testid={`msg-row-${m.id}`} onClick={() => { setActive(m); if (m.status === "new") setStatus(m.id, "read"); }} style={{ borderTop: "1px solid rgba(248,244,240,0.05)", cursor: "pointer" }} onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(139,32,53,0.05)")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                <td style={{ padding: 14, fontSize: "0.85rem", color: "rgba(248,244,240,0.6)" }}>{new Date(m.created_at).toLocaleDateString()}</td>
                <td style={{ padding: 14, color: "var(--warm-off-white)" }}>{m.name}</td>
                <td style={{ padding: 14, color: "rgba(248,244,240,0.7)", fontSize: "0.85rem" }}>{m.email}</td>
                <td style={{ padding: 14, color: "rgba(248,244,240,0.7)", fontSize: "0.85rem" }}>{m.service}</td>
                <td style={{ padding: 14, color: "rgba(248,244,240,0.7)", fontSize: "0.85rem" }}>{m.budget}</td>
                <td style={{ padding: 14 }}>
                  <span className="font-mono" style={{ fontSize: "0.65rem", letterSpacing: "0.15em", padding: "4px 8px", border: "1px solid", borderColor: m.status === "new" ? "var(--gold)" : "rgba(248,244,240,0.2)", color: m.status === "new" ? "var(--gold)" : "rgba(248,244,240,0.6)", textTransform: "uppercase" }}>{m.status}</span>
                </td>
                <td style={{ padding: 14 }}><button data-testid={`msg-delete-${m.id}`} onClick={(e) => { e.stopPropagation(); remove(m.id); }} style={{ background: "transparent", border: "none", color: "rgba(248,244,240,0.5)", cursor: "pointer" }}><Trash2 size={14} /></button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} style={{ padding: 24, textAlign: "center", color: "rgba(248,244,240,0.5)" }}>—</td></tr>}
          </tbody>
        </table>
      </div>

      {active && (
        <div data-testid="msg-detail" onClick={() => setActive(null)} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(10,10,10,0.92)", backdropFilter: "blur(12px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--rich-black-light)", border: "1px solid rgba(201,151,58,0.35)", maxWidth: 720, width: "100%", padding: "40px 44px", maxHeight: "90vh", overflowY: "auto", position: "relative" }}>
            <button onClick={() => setActive(null)} style={{ position: "absolute", top: 16, right: 16, background: "transparent", border: "none", color: "var(--warm-off-white)" }}><X size={22} /></button>
            <h2 className="font-display" style={{ fontSize: "2rem", fontWeight: 400, color: "var(--warm-off-white)" }}>{active.name}</h2>
            <div className="font-mono" style={{ color: "var(--gold)", fontSize: "0.8rem", letterSpacing: "0.15em", marginTop: 8 }}>{active.email} • {active.phone}</div>
            <div style={{ marginTop: 24, padding: 16, background: "rgba(139,32,53,0.1)", border: "1px solid rgba(139,32,53,0.3)", color: "rgba(248,244,240,0.85)", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{active.message}</div>
            <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 24 }}>
              <div><div className="font-mono" style={{ fontSize: "0.65rem", letterSpacing: "0.18em", color: "var(--gold)", textTransform: "uppercase" }}>Company</div><div style={{ color: "var(--warm-off-white)", marginTop: 4 }}>{active.company || "—"}</div></div>
              <div><div className="font-mono" style={{ fontSize: "0.65rem", letterSpacing: "0.18em", color: "var(--gold)", textTransform: "uppercase" }}>Service</div><div style={{ color: "var(--warm-off-white)", marginTop: 4 }}>{active.service}</div></div>
              <div><div className="font-mono" style={{ fontSize: "0.65rem", letterSpacing: "0.18em", color: "var(--gold)", textTransform: "uppercase" }}>Budget</div><div style={{ color: "var(--warm-off-white)", marginTop: 4 }}>{active.budget}</div></div>
              <div><div className="font-mono" style={{ fontSize: "0.65rem", letterSpacing: "0.18em", color: "var(--gold)", textTransform: "uppercase" }}>Date</div><div style={{ color: "var(--warm-off-white)", marginTop: 4 }}>{new Date(active.created_at).toLocaleString()}</div></div>
            </div>
            <div className="flex" style={{ gap: 12, marginTop: 32 }}>
              <button data-testid="msg-mark-read" onClick={() => setStatus(active.id, "read")} className="font-mono" style={{ padding: "10px 18px", background: "transparent", border: "1px solid var(--warm-off-white)", color: "var(--warm-off-white)", fontSize: "0.7rem", letterSpacing: "0.18em", textTransform: "uppercase", cursor: "pointer" }}>{t.admin.status.read}</button>
              <button data-testid="msg-mark-replied" onClick={() => setStatus(active.id, "replied")} className="font-mono" style={{ padding: "10px 18px", background: "var(--gold)", border: "none", color: "var(--rich-black)", fontSize: "0.7rem", letterSpacing: "0.18em", textTransform: "uppercase", cursor: "pointer" }}>{t.admin.status.replied}</button>
              <button onClick={() => remove(active.id)} className="font-mono" style={{ padding: "10px 18px", background: "transparent", border: "1px solid rgba(165,40,69,0.5)", color: "var(--bordeaux-light)", fontSize: "0.7rem", letterSpacing: "0.18em", textTransform: "uppercase", cursor: "pointer", marginLeft: "auto" }}>{t.admin.delete}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;
